import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '../ThemeContext';
import SectionHeader from './SectionHeader';

const skillCategories = [
  {
    title: 'LANGUAGES',
    skills: ['Python', 'C++', 'JavaScript', 'SQL', 'HTML/CSS'],
  },
  {
    title: 'ML & DATA SCIENCE',
    skills: ['PyTorch', 'scikit-learn', 'NumPy/Pandas', 'Jupyter', 'PySPEDAS'],
  },
  {
    title: 'FRAMEWORKS & TOOLS',
    skills: ['React', 'Flask', 'Git', 'AWS', 'PostgreSQL'],
  },
];

function SkillItem({ name, isCyberpunk }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className={`text-xs transition-colors duration-500 ${
        isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-500'
      }`}>{isCyberpunk ? '▹' : '•'}</span>
      <span className={`font-mono text-sm transition-colors duration-500 ${
        isCyberpunk ? 'text-cyber-dim' : 'text-gray-600'
      }`}>{name}</span>
    </div>
  );
}

function SkillCategory({ category, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { isCyberpunk } = useTheme();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`p-6 transition-all duration-500 ${
        isCyberpunk
          ? 'neon-border bg-cyber-dark/50'
          : 'bg-white border border-gray-200 rounded-xl shadow-sm'
      }`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-2 h-2 transition-colors duration-500 ${
          isCyberpunk ? 'bg-cyber-magenta' : 'bg-indigo-500 rounded-full'
        }`} style={isCyberpunk ? { boxShadow: '0 0 8px #ff00ff' } : {}} />
        <h3 className={`font-display text-sm font-semibold tracking-[0.2em] transition-colors duration-500 ${
          isCyberpunk ? 'text-white' : 'text-gray-800'
        }`}>{category.title}</h3>
      </div>

      {category.skills.map((skill) => (
        <SkillItem
          key={skill}
          name={skill}
          isCyberpunk={isCyberpunk}
        />
      ))}
    </motion.div>
  );
}

export default function Skills() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { isCyberpunk } = useTheme();

  return (
    <section id="skills" className={`relative py-24 px-6 transition-colors duration-700 ${
      isCyberpunk ? 'bg-cyber-black' : 'bg-white'
    }`}>
      <div className="max-w-6xl mx-auto">
        <SectionHeader number="04." title="SKILLS" />

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {skillCategories.map((category, i) => (
            <SkillCategory key={category.title} category={category} index={i} />
          ))}
        </div>

        {/* Coursework */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className={`p-6 transition-all duration-500 ${
            isCyberpunk
              ? 'neon-border bg-cyber-dark/30'
              : 'bg-white border border-gray-200 rounded-xl shadow-sm'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-2 h-2 transition-colors duration-500 ${
              isCyberpunk ? 'bg-cyber-magenta' : 'bg-indigo-500 rounded-full'
            }`} style={isCyberpunk ? { boxShadow: '0 0 8px #ff00ff' } : {}} />
            <h3 className={`font-display text-sm font-semibold tracking-[0.2em] transition-colors duration-500 ${
              isCyberpunk ? 'text-white' : 'text-gray-800'
            }`}>RELEVANT COURSEWORK</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              'Data Structures', 'Algorithms', 'Computer Systems',
              'Artificial Intelligence', 'Machine Learning', 'Data Science',
              'Computer Vision', 'Deep Learning',
            ].map((course) => (
              <motion.span
                key={course}
                whileHover={{ scale: 1.05 }}
                className={`font-mono text-xs px-3 py-2 border cursor-default transition-all duration-500 ${
                  isCyberpunk
                    ? 'border-cyber-magenta/15 text-cyber-dim/80 bg-cyber-magenta/5 hover:text-cyber-magenta'
                    : 'border-gray-200 text-gray-600 bg-gray-50 hover:text-indigo-600 hover:border-indigo-300 rounded-lg'
                }`}
              >
                {course}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>

      {isCyberpunk ? (
        <div className="cyber-divider mt-24 max-w-4xl mx-auto" />
      ) : (
        <div className="mt-24 max-w-4xl mx-auto h-[1px] bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      )}
    </section>
  );
}
